import { bsConstants } from 'booksprout'

import { useI18n } from '../modules/i18n/i18nModule'

/**
 * The translators as plain functions, usable during `setup()`.
 *
 * `vm.$tc(...)` looks like the obvious thing and is not available when a setup body needs it:
 * vue-i18n 9's legacy API installs `$t`, `$tc` and `$te` from a global mixin's `created` hook, and
 * Vue 3 runs `setup()` BEFORE `created`. So a page that translates while setting up -- Plans builds
 * its pricing table there so the table is in the server-rendered HTML -- gets `undefined` for the
 * property, or `$tc is not a function` if it merely deferred reading it. Under Vue 2 the mixin ran
 * first and there was nothing to notice.
 *
 * These go to the app-level instance instead (`i18n.global`, held by the i18n module), which exists
 * from boot and does not care where it is called from. That is the same path the API error handler
 * has always used to translate error codes from outside a component.
 *
 * (The specs did not catch the instance version: `withGlobals` puts `$tc` on `globalProperties`,
 * where it IS visible during setup. Loading a real page in a browser is what found it.)
 */
export type Translate = (key: string, ...args: any[]) => string

export const translate: Translate = (key, ...args) => useI18n().tc(key, ...args)

export const translateExists = (key: string): boolean => useI18n().te(key)

/**
 * Which locale is showing, for the strings vue-i18n cannot translate: the category names come from
 * the database, so the filter panels pick a column by locale rather than looking up a key. [sc-27608]
 *
 * Off the app-level instance for the same reason as `translate` — the panels re-read it whenever they
 * rebuild their options, including from an api callback that lands after the component has gone, and
 * `vm.$i18n` is not there by then. Falls back to the default locale rather than an empty string,
 * which would select no column at all.
 */
export const currentLocale = (): string => useI18n().i18n()?.locale ?? bsConstants.I18N.DEFAULT_LOCALE
